import { useState } from "react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { USTA_TITLES } from "@/lib/titles";
import { Pencil } from "lucide-react";
import { toast } from "sonner";

export function AdminUstaControls({ usta, onSaved }) {
  const { user, impersonating } = useAuth();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState(usta.name || "");
  const [phone, setPhone] = useState("");
  const [specialty, setSpecialty] = useState(usta.specialty || "");
  const [extra, setExtra] = useState(usta.extra_titles || []);
  const [reason, setReason] = useState("");
  const uid = usta.user_id;

  if (!(user && user.role === "admin") || impersonating) return null;

  const toggleExtra = (t) => setExtra((e) => e.includes(t) ? e.filter((x) => x !== t) : (e.length >= 3 ? (toast.warning("En fazla 3 ek uzmanlık"), e) : [...e, t]));

  const save = async () => {
    if (!reason.trim()) { toast.error("Müdahale gerekçesi zorunludur"); return; }
    try {
      if (name.trim() && name.trim() !== usta.name) await api.put(`/admin/users/${uid}/name`, { name: name.trim(), reason });
      if (phone.trim()) await api.put(`/admin/users/${uid}/phone`, { phone: phone.trim(), reason });
      if (specialty) await api.put(`/admin/ustalar/${uid}/titles`, { specialty, extra_titles: extra.filter((t) => t !== specialty), reason });
      toast.success("Usta profili güncellendi");
      setReason(""); setPhone(""); setOpen(false); onSaved && onSaved();
    } catch (e) { toast.error(apiError(e)); }
  };

  const removePhoto = async () => {
    if (!reason.trim()) { toast.error("Müdahale gerekçesi zorunludur"); return; }
    try { await api.post(`/admin/ustalar/${uid}/remove-photo`, { reason }); toast.success("Fotoğraf kaldırıldı"); setOpen(false); onSaved && onSaved(); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <>
      <Button onClick={() => setOpen(true)} data-testid="admin-usta-edit" className="bg-violet-600 hover:bg-violet-700 text-white rounded-xl h-9">
        <Pencil className="h-4 w-4 mr-1" /> Admin Düzenle
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-lg rounded-2xl max-h-[85vh] overflow-y-auto" data-testid="admin-usta-modal">
          <DialogHeader><DialogTitle>Yönetici — Usta Profili Düzenle</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Ad Soyad</Label><Input value={name} onChange={(e) => setName(e.target.value)} className="rounded-xl mt-1" data-testid="admin-usta-name" /></div>
            <div><Label>Telefon (değiştirmek için girin)</Label><Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Boş bırakılırsa değişmez" className="rounded-xl mt-1" data-testid="admin-usta-phone" /></div>
            <div><Label>Ana Unvan</Label>
              <Select value={specialty} onValueChange={(v) => { setSpecialty(v); setExtra((x) => x.filter((t) => t !== v)); }}>
                <SelectTrigger className="rounded-xl mt-1" data-testid="admin-usta-specialty"><SelectValue placeholder="Seçin" /></SelectTrigger>
                <SelectContent className="max-h-64">{USTA_TITLES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Ek Uzmanlıklar ({extra.length}/3)</Label>
              <div className="flex flex-wrap gap-2 mt-2 max-h-40 overflow-y-auto p-2 rounded-xl border border-slate-100 bg-slate-50/50">
                {USTA_TITLES.filter((t) => t !== specialty).map((t) => {
                  const a = extra.includes(t);
                  return <button key={t} type="button" onClick={() => toggleExtra(t)} className={`text-xs font-semibold px-3 py-1.5 rounded-full border ${a ? "bg-emerald-500 text-white border-emerald-500" : "bg-white text-slate-600 border-slate-200"}`}>{t}</button>;
                })}
              </div>
            </div>
            <div><Label>Müdahale Gerekçesi (zorunlu)</Label><Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Destek Talep No / Açıklama" className="rounded-xl mt-1" data-testid="admin-usta-reason" /></div>
            <div className="flex gap-2 pt-1">
              <Button onClick={save} className="brand-gradient text-white rounded-xl flex-1" data-testid="admin-usta-save">Kaydet</Button>
              <Button onClick={removePhoto} variant="outline" className="rounded-xl border-red-200 text-red-600 hover:bg-red-50" data-testid="admin-usta-remove-photo">Fotoğrafı Kaldır</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
